import { mergeRefs } from "@/client/react";
import type { PropsOf } from "@pinyinly/lib/types";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { TextInputInstance } from "react-native";
import { TextInput } from "react-native";
import { tv } from "tailwind-variants";
import type { TextInputVariant } from "./TextInputSingle";

interface TextInputMultiProps extends Omit<
  PropsOf<typeof TextInput>,
  // make `placeholder` mandatory (encourage a11y)
  `placeholder` | `multiline` | `textAlignVertical`
> {
  disabled?: boolean;
  placeholder: string | undefined;
  autoResizeMinHeight?: number;
  variant?: TextInputVariant;
}

export function TextInputMulti({
  variant = `flat`,
  ref,
  ...props
}: TextInputMultiProps) {
  const inputRef = useRef<TextInputInstance>(null);
  const autoResizeMinHeight = props.autoResizeMinHeight ?? 60;

  const adjustHeight = useCallback(() => {
    if (typeof window === `undefined`) {
      return;
    }

    const element = inputRef.current as HTMLTextAreaElement | null;
    if (!element || element.tagName !== `TEXTAREA`) {
      return;
    }

    element.style.height = `auto`;
    element.style.height = `${Math.max(element.scrollHeight, autoResizeMinHeight)}px`;
  }, [autoResizeMinHeight]);

  // On web, auto-resize the textarea to fit content
  useEffect(() => {
    if (typeof window === `undefined`) {
      return;
    } // Skip on native

    const element = inputRef.current as HTMLTextAreaElement | null;
    if (!element) {
      return;
    } // Not a textarea

    const textarea = element;
    if (textarea.tagName !== `TEXTAREA`) {
      return;
    }

    adjustHeight();
    textarea.addEventListener(`input`, adjustHeight);
    return () => {
      textarea.removeEventListener(`input`, adjustHeight);
    };
  }, [adjustHeight]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight, props.value]);

  return (
    <TextInput
      {...props}
      ref={mergeRefs(ref, inputRef)}
      multiline
      // @ts-expect-error `dataSet` is a react-native-web-specific prop.
      dataSet={{
        // Disable the 1Password button in inputs.
        "1p-ignore": `true`,
      }}
      style={[props.style, { overflow: `visible` }]}
      className={inputClass({
        textAlign: props.textAlign,
        className: props.className,
        variant,
      })}
      placeholderTextColorClassName={inputPlaceholderTextColorClass({
        variant,
      })}
    />
  );
}

const inputPlaceholderTextColorClass = tv({
  base: `accent-muted-fg`,
  variants: {
    variant: {
      bare: `accent-muted-fg`,
      flat: `accent-fg-bg30`,
    },
  },
});

const inputClass = tv({
  base: `
    resize-none

    web:block
  `,
  variants: {
    textAlign: {
      left: `text-left`,
      center: `text-center`,
      right: `text-right`,
      start: `text-start`,
      end: `text-end`,
    },
    variant: {
      bare: `font-sans text-fg outline-none`,
      flat: `rounded-xl bg-bg-high px-4 py-3 pyly-body-input outline-none`,
    },
  },
});
